import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { generateLevel, getCipherDisplayName, getCipherIcon } from '../game/levels';
import Footer from './Footer';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Computes score for a level
 */
function computeScore({ level, guessesLeft, guessesAllowed, hintsUsed, elapsedSeconds }) {
  const base = 100 * level;
  const guessFactor = guessesLeft / guessesAllowed;
  const hintPenalty = Math.max(0.5, 1 - 0.2 * hintsUsed); // 20% less per hint, min 50%
  const timeFactor = elapsedSeconds ? Math.max(0.5, 1.5 - elapsedSeconds / 60) : 1; // faster = more points

  return Math.round(base * guessFactor * hintPenalty * timeFactor);
}

/**
 * Load game progress from localStorage
 */
function loadProgress() {
  try {
    const saved = localStorage.getItem('cipherGameProgress');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Failed to load progress:', e);
  }
  return {
    highestLevel: 1,
    scores: {},
    totalScore: 0
  };
}

/**
 * Save game progress to localStorage
 */
function saveProgress(progress) {
  try {
    localStorage.setItem('cipherGameProgress', JSON.stringify(progress));
  } catch (e) {
    console.error('Failed to save progress:', e);
  }
}

function CipherGame({ onBackToUtility }) {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [levelData, setLevelData] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);
  const [gameProgress, setGameProgress] = useState(loadProgress());
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [levelScore, setLevelScore] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Initialize level
  useEffect(() => {
    const level = generateLevel(currentLevel);
    
    // Set start time
    level.startTime = Date.now();
    
    // For decrypt mode, we need to encrypt the plaintext first to get ciphertext
    if (level.mode === 'decrypt') {
      initializeDecryptLevel(level);
    } else {
      // For encrypt mode, challenge is the plaintext
      level.challengeText = level.plaintext;
      level.answerText = ''; // Will be computed by user
      setLevelData(level);
    }
  }, [currentLevel]);

  // Timer
  useEffect(() => {
    if (!levelData || levelData.completed) return;
    
    const interval = setInterval(() => {
      if (levelData.startTime) {
        setElapsedTime(Math.floor((Date.now() - levelData.startTime) / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [levelData]);

  /**
   * Initialize decrypt level by encrypting plaintext
   */
  const initializeDecryptLevel = async (level) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/encrypt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: level.plaintext,
          cipher_type: level.cipher,
          key: level.key
        })
      });

      if (!response.ok) {
        throw new Error('Failed to initialize level');
      }

      const data = await response.json();
      level.challengeText = data.result;
      level.answerText = level.plaintext;
      setLevelData(level);
    } catch (err) {
      setError(`Failed to initialize level: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Validate user's answer
   */
  const validateAnswer = async () => {
    if (!userAnswer.trim()) {
      setError('Please enter an answer');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const endpoint = levelData.mode === 'encrypt' ? '/encrypt' : '/decrypt';
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: levelData.mode === 'encrypt' ? levelData.plaintext : levelData.challengeText,
          cipher_type: levelData.cipher,
          key: levelData.key
        })
      });

      if (!response.ok) {
        throw new Error('Validation failed');
      }

      const data = await response.json();
      const correctAnswer = data.result.trim().toUpperCase();
      const userAnswerUpper = userAnswer.trim().toUpperCase();

      if (correctAnswer === userAnswerUpper) {
        // Correct answer!
        handleCorrectAnswer();
      } else {
        // Wrong answer
        handleWrongAnswer();
      }
    } catch (err) {
      setError(`Validation error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle correct answer
   */
  const handleCorrectAnswer = () => {
    const endTime = Date.now();
    const elapsedSeconds = levelData.startTime ? (endTime - levelData.startTime) / 1000 : 0;
    
    const score = computeScore({
      level: currentLevel,
      guessesLeft: levelData.guessesLeft,
      guessesAllowed: levelData.guessesAllowed,
      hintsUsed: levelData.hintsUsed,
      elapsedSeconds
    });

    setLevelScore(score);
    setShowSuccess(true);

    // Update progress
    const newProgress = {
      ...gameProgress,
      highestLevel: Math.max(gameProgress.highestLevel, currentLevel + 1),
      scores: {
        ...gameProgress.scores,
        [currentLevel]: score
      },
      totalScore: (gameProgress.totalScore || 0) + score
    };
    setGameProgress(newProgress);
    saveProgress(newProgress);

    // Mark level as completed
    setLevelData({ ...levelData, completed: true, score, endTime });
  };

  /**
   * Handle wrong answer
   */
  const handleWrongAnswer = () => {
    const newGuessesLeft = levelData.guessesLeft - 1;
    
    if (newGuessesLeft <= 0) {
      setShowFailure(true);
      setLevelData({ ...levelData, guessesLeft: 0, completed: true });
    } else {
      setLevelData({ ...levelData, guessesLeft: newGuessesLeft });
      setShowFailure(true);
      setTimeout(() => setShowFailure(false), 2000);
    }
  };

  /**
   * Show next hint
   */
  const showNextHint = () => {
    if (currentHintIndex < levelData.hints.length) {
      setShowHint(true);
      setCurrentHintIndex(currentHintIndex + 1);
      setLevelData({ ...levelData, hintsUsed: levelData.hintsUsed + 1 });
    }
  };

  /**
   * Move to next level
   */
  const nextLevel = () => {
    if (currentLevel < 15) {
      setCurrentLevel(currentLevel + 1);
      setUserAnswer('');
      setShowSuccess(false);
      setShowFailure(false);
      setShowHint(false);
      setCurrentHintIndex(0);
      setLevelScore(0);
      setElapsedTime(0);
      setError('');
    }
  };

  /**
   * Retry current level
   */
  const retryLevel = () => {
    const level = generateLevel(currentLevel);
    level.startTime = Date.now();
    
    if (level.mode === 'decrypt') {
      initializeDecryptLevel(level);
    } else {
      setLevelData(level);
    }
    
    setUserAnswer('');
    setShowSuccess(false);
    setShowFailure(false);
    setShowHint(false);
    setCurrentHintIndex(0);
    setLevelScore(0);
    setElapsedTime(0);
    setError('');
  };

  /**
   * Reset progress
   */
  const resetProgress = () => {
    if (window.confirm('Are you sure you want to reset all progress?')) {
      localStorage.removeItem('cipherGameProgress');
      setGameProgress({ highestLevel: 1, scores: {}, totalScore: 0 });
      setCurrentLevel(1);
      retryLevel();
    }
  };

  if (!levelData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <motion.div
          className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  const cipherName = getCipherDisplayName(levelData.cipher);
  const cipherIcon = getCipherIcon(levelData.cipher);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 relative overflow-hidden flex flex-col">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-30"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              y: [null, window.innerHeight + 100],
              opacity: [0.3, 0, 0.3],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 py-8 pb-20 relative z-10 max-w-6xl">
        {/* Header */}
        <motion.header
          className="text-center mb-8"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 mb-4"
            animate={{
              textShadow: [
                '0 0 20px rgba(139, 92, 246, 0.5)',
                '0 0 30px rgba(139, 92, 246, 0.8)',
                '0 0 20px rgba(139, 92, 246, 0.5)',
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            CryptGame: CipherQuest
          </motion.h1>
          <div className="flex items-center justify-center gap-4 text-white flex-wrap">
            <span className="text-xl font-semibold">Total Score: <span className="text-yellow-400">{gameProgress.totalScore || 0}</span></span>
            <button
              onClick={onBackToUtility}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors backdrop-blur-sm"
            >
              Utility Mode
            </button>
            <button
              onClick={resetProgress}
              className="px-4 py-2 bg-red-500/50 hover:bg-red-500/70 rounded-lg transition-colors backdrop-blur-sm"
            >
              Reset Progress
            </button>
          </div>
        </motion.header>

        {/* Level Info Card */}
        <motion.div
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-6 border border-white/20 shadow-2xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="text-4xl">{cipherIcon}</div>
              <div>
                <h2 className="text-2xl font-bold text-white">Level {currentLevel}</h2>
                <p className="text-cyan-300">{cipherName}</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-white">
              <div>
                <span className="text-sm text-gray-300">Guesses Left:</span>
                <span className="ml-2 text-xl font-bold text-yellow-400">{levelData.guessesLeft}</span>
              </div>
              {elapsedTime > 0 && (
                <div>
                  <span className="text-sm text-gray-300">Time:</span>
                  <span className="ml-2 text-xl font-bold text-green-400">
                    {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Main Game Card */}
        <motion.div
          className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: 1, 
            y: 0,
            scale: showFailure ? [1, 1.05, 1] : 1,
            boxShadow: showSuccess ? '0 0 50px rgba(34, 197, 94, 0.5)' : '0 0 20px rgba(0, 0, 0, 0.3)'
          }}
          transition={{ 
            duration: 0.3,
            boxShadow: { duration: 0.5 }
          }}
        >
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              {levelData.mode === 'encrypt' ? '🔐 Encrypt this message:' : '🔓 Decrypt this ciphertext:'}
            </h3>
            <div className="bg-black/30 rounded-lg p-4 mb-4">
              <p className="text-2xl font-mono text-cyan-300 break-words">
                {levelData.mode === 'encrypt' ? levelData.plaintext : levelData.challengeText}
              </p>
            </div>
          </div>

          {error && (
            <motion.div
              className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-4 text-red-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {error}
            </motion.div>
          )}

          <div className="mb-6">
            <label className="block text-white font-semibold mb-2">Your Answer:</label>
            <motion.input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isLoading && validateAnswer()}
              disabled={levelData.completed || isLoading}
              className="w-full px-4 py-3 bg-black/30 border border-white/30 rounded-lg text-white font-mono text-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
              whileFocus={{ scale: 1.02 }}
            />
          </div>

          <div className="flex gap-4 flex-wrap">
            <motion.button
              onClick={validateAnswer}
              disabled={levelData.completed || isLoading}
              className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: levelData.completed || isLoading ? 1 : 1.05 }}
              whileTap={{ scale: levelData.completed || isLoading ? 1 : 0.95 }}
            >
              {isLoading ? 'Validating...' : 'Submit Answer'}
            </motion.button>

            {!levelData.completed && currentHintIndex < levelData.hints.length && (
              <motion.button
                onClick={showNextHint}
                className="px-6 py-3 bg-yellow-500/80 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                💡 Hint ({levelData.hints.length - currentHintIndex} left)
              </motion.button>
            )}

            {levelData.completed && (
              <motion.button
                onClick={currentLevel < 15 ? nextLevel : () => {}}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {currentLevel < 15 ? 'Next Level →' : '🎉 Game Complete!'}
              </motion.button>
            )}

            {levelData.completed && levelData.guessesLeft === 0 && (
              <motion.button
                onClick={retryLevel}
                className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🔄 Retry Level
              </motion.button>
            )}
          </div>

          {showHint && currentHintIndex > 0 && (
            <motion.div
              className="mt-4 bg-yellow-500/20 border border-yellow-500 rounded-lg p-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-yellow-200">
                <strong>Hint {currentHintIndex}:</strong> {levelData.hints[currentHintIndex - 1]}
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Success Animation */}
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSuccess(false)}
            >
              <motion.div
                className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 text-center max-w-md mx-4"
                initial={{ scale: 0.5, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0.5, rotate: 180 }}
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div
                  className="text-6xl mb-4"
                  animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  ✅
                </motion.div>
                <h3 className="text-3xl font-bold text-white mb-2">Correct!</h3>
                <p className="text-xl text-green-100 mb-4">Level {currentLevel} Complete!</p>
                <p className="text-2xl font-bold text-yellow-300 mb-4">
                  Score: {levelScore} points
                </p>
                {levelData.guessesLeft === levelData.guessesAllowed && levelData.hintsUsed === 0 && (
                  <p className="text-green-200 font-semibold">🌟 Flawless!</p>
                )}
                <button
                  onClick={() => {
                    setShowSuccess(false);
                    if (currentLevel < 15) nextLevel();
                  }}
                  className="mt-4 px-6 py-2 bg-white text-green-600 font-bold rounded-lg hover:bg-gray-100"
                >
                  Continue
                </button>
                <motion.p
                  className="mt-4 text-xs text-green-100/70"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  Keep going! You're doing great! 🚀
                </motion.p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Level Progress */}
        <div className="grid grid-cols-5 md:grid-cols-15 gap-2 max-w-4xl mx-auto">
          {Array.from({ length: 15 }, (_, i) => {
            const levelNum = i + 1;
            const isUnlocked = levelNum <= gameProgress.highestLevel;
            const isCurrent = levelNum === currentLevel;
            const score = gameProgress.scores[levelNum];

            return (
              <motion.div
                key={levelNum}
              className={`p-2 rounded-lg text-center text-xs md:text-sm ${
                isCurrent
                  ? 'bg-purple-500 text-white ring-2 ring-yellow-400'
                  : isUnlocked
                  ? 'bg-green-500/50 text-white'
                  : 'bg-gray-700/50 text-gray-400'
              }`}
                whileHover={{ scale: isUnlocked ? 1.1 : 1 }}
                onClick={() => isUnlocked && setCurrentLevel(levelNum)}
                style={{ cursor: isUnlocked ? 'pointer' : 'not-allowed' }}
              >
                <div className="font-bold">{levelNum}</div>
                {score && <div className="text-xs">⭐{score}</div>}
              </motion.div>
            );
          })}
        </div>
        
        {/* Footer */}
        <Footer variant="game" />
      </div>
    </div>
  );
}

export default CipherGame;

